(() => {
  const OFFER_SELECTOR = '[data-stylux-offer]';
  const CARD_LABEL_SELECTOR = '[data-stylux-label="card"]:not([data-stylux-resolved])';
  const PRODUCT_LABEL_SELECTOR = '[data-stylux-label="product"]';
  const RESOLVED_ATTRIBUTE = 'data-stylux-resolved';
  const OBSERVED_ROOT_ID = 'MainContent';
  const OFFER_CHANNEL = 'D2C';
  const AVAILABLE_OFFER_STATES = new Set(['ACTIVE', 'MANUALLY_SET_TO_ACTIVE']);
  const READY_TIMEOUT = 10000;
  const LOOKUP_BATCH_SIZE = 30;
  const SCAN_DEBOUNCE = 100;

  const availabilityByVariantId = new Map();
  let readySdk = null;
  let queuedScan = null;
  let scanTimer = null;

  const withTimeout = (promise, timeout) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), timeout))]);

  const whenScriptLoaded = () =>
    new Promise((resolve) => {
      if (!window.Stylux) {
        resolve(null);
        return;
      }

      window.Stylux.onScriptLoad(() => resolve(window.Stylux));
    });

  const whenReady = async () => {
    const sdk = await withTimeout(whenScriptLoaded(), READY_TIMEOUT);
    if (!sdk) return null;

    try {
      const isSetup = await withTimeout(sdk.setupComplete().then(() => true), READY_TIMEOUT);
      return isSetup ? sdk : null;
    } catch (error) {
      console.error('Stylux SDK setup failed:', error);
      return null;
    }
  };

  const getSdk = () => {
    if (!readySdk) readySdk = whenReady();
    return readySdk;
  };

  const chunk = (items, size) =>
    items.reduce((batches, item, index) => {
      if (index % size === 0) batches.push([]);
      batches[batches.length - 1].push(item);
      return batches;
    }, []);

  const firstUsableOffer = (connection) =>
    (connection?.nodes ?? []).find(
      (offer) => offer.fulfillmentType !== 'ON_DEMAND' && offer.channel === OFFER_CHANNEL
    ) ?? null;

  const pickOffer = (sdk, product) => {
    const liveOffer = firstUsableOffer(product.liveOffers);
    const stagedOffer = firstUsableOffer(product.stagedOffers);
    const stagedIsNewer = stagedOffer && (!liveOffer || stagedOffer.version > liveOffer.version);

    return sdk.previews?.isEnabled && stagedIsNewer ? stagedOffer : liveOffer;
  };

  const hasPersonalizationOffer = (sdk, product) => {
    const offer = pickOffer(sdk, product);

    return (
      Boolean(offer) &&
      AVAILABLE_OFFER_STATES.has(offer.activeState) &&
      (offer.options?.nodes?.length ?? 0) > 0
    );
  };

  const lookupBatch = async (sdk, variantIds) => {
    const { products } = await sdk.gql.queries.productsWithOffers({
      variables: {
        numProducts: variantIds.length,
        numImageAssets: 1,
        numOffers: 5,
        numOfferOptions: 1,
        offerChannels: [OFFER_CHANNEL],
        productsFilter: { productIds: variantIds },
      },
    });

    return (products?.nodes ?? []).reduce((availability, product) => {
      const isAvailable = hasPersonalizationOffer(sdk, product);
      [product.productId, product.id]
        .filter(Boolean)
        .forEach((key) => availability.set(String(key), isAvailable));
      return availability;
    }, new Map());
  };

  const lookupAvailability = (sdk, variantIds) => {
    const pending = variantIds.filter((variantId) => !availabilityByVariantId.has(variantId));

    chunk(pending, LOOKUP_BATCH_SIZE).forEach((batch) => {
      const batchAvailability = lookupBatch(sdk, batch).catch((error) => {
        console.error('Stylux personalization label lookup failed:', error);
        batch.forEach((variantId) => availabilityByVariantId.delete(variantId));
        return new Map();
      });

      batch.forEach((variantId) =>
        availabilityByVariantId.set(
          variantId,
          batchAvailability.then((availability) => availability.get(variantId) === true)
        )
      );
    });

    return Promise.all(
      variantIds.map((variantId) => availabilityByVariantId.get(variantId) ?? false)
    );
  };

  const isPersonalizable = async (sdk, variantId) => {
    const [isAvailable] = await lookupAvailability(sdk, [variantId]);
    return isAvailable;
  };

  const resolveLabel = (label, isAvailable) => {
    label.setAttribute(RESOLVED_ATTRIBUTE, '');
    label.hidden = !isAvailable;
  };

  const runCardScan = async () => {
    const labels = Array.from(document.querySelectorAll(CARD_LABEL_SELECTOR));
    if (!labels.length) return;

    const sdk = await getSdk();
    if (!sdk) {
      labels.forEach((label) => resolveLabel(label, false));
      return;
    }

    const variantIds = [
      ...new Set(labels.map((label) => label.dataset.styluxVariantId).filter(Boolean)),
    ];
    const availability = await lookupAvailability(sdk, variantIds);
    const availabilityByLookedUpId = new Map(
      variantIds.map((variantId, index) => [variantId, availability[index]])
    );

    labels.forEach((label) =>
      resolveLabel(label, availabilityByLookedUpId.get(label.dataset.styluxVariantId) === true)
    );
  };

  const scanCards = () => {
    queuedScan = (queuedScan ?? Promise.resolve()).then(runCardScan).catch(() => {});
    return queuedScan;
  };

  const scheduleCardScan = () => {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scanCards, SCAN_DEBOUNCE);
  };

  const showOffer = async () => {
    const container = document.querySelector(OFFER_SELECTOR);
    if (!container) return;

    const label = document.querySelector(PRODUCT_LABEL_SELECTOR);
    const variantId = container.dataset.styluxVariantId;
    const sdk = variantId ? await getSdk() : null;

    if (!sdk) {
      if (label) resolveLabel(label, false);
      return;
    }

    try {
      const [isAvailable] = await Promise.all([
        isPersonalizable(sdk, variantId),
        sdk.offers.show({
          productId: variantId,
          elementClasses: container.dataset.styluxOfferClasses ?? '',
        }),
      ]);
      if (label) resolveLabel(label, isAvailable);
    } catch (error) {
      if (label) resolveLabel(label, false);
      console.error('Stylux offer failed to render:', error);
    }
  };

  const init = () => {
    const observedRoot = document.getElementById(OBSERVED_ROOT_ID);
    if (observedRoot) {
      new MutationObserver(scheduleCardScan).observe(observedRoot, {
        childList: true,
        subtree: true,
      });
    }

    scanCards();
    showOffer();
    document.addEventListener('variant-updated', showOffer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
